import { z } from 'zod'
import { SourceId } from './id.js'

/**
 * Een bron in de bronnenopslag van een sectiedocument (R-50, eisenboek 12.9).
 *
 * De inhoud is CSL-JSON en geen eigen, vertaald schema: de citatie-engine (citeproc-js) leest
 * en de literatuurlijst schrijft in die vorm, en CSL-JSON is een extern formaat met eigen
 * veldnamen (`container-title`, `issued`) en geen domeinbegrip van dit product. Alleen `id`
 * is van ons: een `SourceId`, niet de vrije `id`-string die CSL-JSON toestaat.
 *
 * Bewust een klein deel van de volledige CSL-JSON-spec, passend bij de typen die een
 * aanvraag- of beoordelingsformulier citeert. Een `type` dat hier ontbreekt is een latere
 * uitbreiding van deze enum, geen modelwijziging elders.
 *
 * Bronnen komen in dit product alleen van de invuller zelf (handmatige invoer). DOI- of
 * BibTeX-import zoals in de CitatenPOC is een bewuste, latere uitbreiding: dat voegt een
 * uitgaande aanroep vanuit de publicatie-pod toe, en dat is een eigen afweging.
 */
export const CslName = z
  .strictObject({
    family: z.string().min(1).optional(),
    given: z.string().min(1).optional(),
    /** Voor een organisatie als auteur, in plaats van `family`/`given` (CSL-JSON). */
    literal: z.string().min(1).optional(),
  })
  .refine(
    (name) => name.literal !== undefined || name.family !== undefined,
    'een naam heeft een achternaam of is een organisatienaam via literal',
  )
export type CslName = z.infer<typeof CslName>

/** Een datum in CSL-JSON: geneste `date-parts`, want een stijl mag op jaar alleen afkorten. */
export const CslDate = z.strictObject({
  'date-parts': z.array(z.array(z.number().int())).min(1),
})
export type CslDate = z.infer<typeof CslDate>

export const SourceType = z.enum(['article-journal', 'book', 'chapter', 'report', 'webpage'])
export type SourceType = z.infer<typeof SourceType>

export const Source = z.strictObject({
  id: SourceId,
  type: SourceType,
  title: z.string().min(1),
  author: z.array(CslName).optional(),
  issued: CslDate.optional(),
  'container-title': z.string().min(1).optional(),
  publisher: z.string().min(1).optional(),
  page: z.string().min(1).optional(),
  URL: z.string().min(1).optional(),
  DOI: z.string().min(1).optional(),
})
export type Source = z.infer<typeof Source>
